import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useTranslation } from "react-i18next";
import { GET_USER_INFO, POST_USER_INFO } from "services/users";
import { BeatLoader } from "react-spinners";

const getLineCallbackErrorKey = (errorParam, errorDescription = "") => {
  if (
    errorParam === "already_linked" ||
    errorDescription.toLowerCase().includes("already")
  ) {
    return "line_error_already_linked";
  }

  if (
    errorParam === "session_expired" ||
    errorDescription.toLowerCase().includes("session") ||
    errorDescription.toLowerCase().includes("expired")
  ) {
    return "line_error_session_expired";
  }

  return "line_error_default";
};

const hasLineIdentity = (userInfo) =>
  Array.isArray(userInfo?.identities) &&
  userInfo.identities.some((identity) => identity?.provider === "line");

const getStoredToken = (tokenResponse) => {
  if (typeof tokenResponse === "string") {
    return tokenResponse;
  }

  if (typeof tokenResponse?.id_token === "string") {
    return tokenResponse.id_token;
  }

  return null;
};

const LineCallback = () => {
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const { getAccessTokenSilently } = useAuth0();
  const { t } = useTranslation();

  useEffect(() => {
    const handleCallback = async () => {
      const params = new URLSearchParams(window.location.search);
      const errorParam = params.get("error");
      const errorDescription = params.get("error_description") || "";
      const callbackErrorKey = errorParam
        ? getLineCallbackErrorKey(errorParam, errorDescription)
        : null;

      try {
        console.info("[LINE callback][Coupon] start", {
          hasCallbackError: Boolean(callbackErrorKey),
          errorParam,
        });

        let latestToken = null;

        try {
          latestToken = getStoredToken(
            await getAccessTokenSilently({
              detailedResponse: true,
              cacheMode: "off",
            })
          );
        } catch (tokenError) {
          console.warn("[LINE callback][Coupon] token refresh failed", {
            message: tokenError?.message,
            status: tokenError?.status ?? tokenError?.response?.status,
          });
        }

        if (!latestToken) {
          latestToken = getStoredToken(
            await getAccessTokenSilently({ detailedResponse: true })
          );
        }

        if (!latestToken) {
          throw new Error("Missing id_token after LINE callback");
        }

        localStorage.setItem("__token__", latestToken);

        const res = await GET_USER_INFO();
        if (!res) {
          throw new Error("Missing user info after LINE callback");
        }
        console.info("[LINE callback][Coupon] first userinfo", {
          identitiesCount: Array.isArray(res?.identities) ? res.identities.length : 0,
          hasLineId: Boolean(res?.line_id),
          isLineLinked: hasLineIdentity(res),
        });

        console.info("[LINE callback][Coupon] post userinfo start");
        await POST_USER_INFO(res);
        console.info("[LINE callback][Coupon] post userinfo success");

        const refreshedUserInfo = await GET_USER_INFO();
        const userInfoToStore = refreshedUserInfo ?? res;
        const isLineLinked = hasLineIdentity(userInfoToStore);

        console.info("[LINE callback][Coupon] final userinfo", {
          identitiesCount: Array.isArray(userInfoToStore?.identities)
            ? userInfoToStore.identities.length
            : 0,
          hasLineId: Boolean(userInfoToStore?.line_id),
          isLineLinked,
        });

        localStorage.setItem("userInfo", JSON.stringify(userInfoToStore));

        if (callbackErrorKey && !isLineLinked) {
          setError(callbackErrorKey);
          return;
        }

        navigate("/", { replace: true });
      } catch (err) {
        console.error("[LINE callback][Coupon] sync failed", {
          message: err?.message,
          status: err?.status ?? err?.response?.status,
          code:
            err?.code ??
            (typeof err?.response?.data?.code === "string"
              ? err.response.data.code
              : typeof err?.response?.data?.message === "string"
                ? err.response.data.message
                : undefined),
          hasCallbackError: Boolean(callbackErrorKey),
        });
        setError(callbackErrorKey ?? "line_error_unexpected");
      }
    };

    handleCallback();
  }, [navigate, getAccessTokenSilently]);

  if (error) {
    return (
      <div className="w-full lg:max-w-mobile m-auto min-h-screen flex flex-col items-center justify-center gap-6 px-6 font-primary">
        <p className="text-center text-sm text-red-500">{t(error)}</p>
        <button
          onClick={() => navigate("/", { replace: true })}
          className="justify-center py-3 px-6 text-sm text-center text-white bg-black rounded-lg hover:scale-105"
        >
          {t("line_back_to_mypage")}
        </button>
      </div>
    );
  }

  return (
    <div className="w-full h-screen flex flex-col justify-center">
      <div aria-label="Loading..." role="status" className="mx-auto">
        <BeatLoader size={20} color={"#404040"} />
      </div>
    </div>
  );
};

export default LineCallback;